/**
 * 영업건 고르기 모달 (v2.94.0) — 문서를 영업건에 연동할 때 쓰는 공용 창.
 *
 * 왜 만들었나:
 *   NAS 보관 경로가 「{연도}\{제안업체}\{영업건번호}_{사업명}」 이라, 영업건 연동이 없으면
 *   저장할 곳 자체가 없다. 그래서 [NAS 저장] 을 눌렀는데 미연동이면 이 창으로 바로 연결한다.
 *
 * 기존 LinkSalesModal 과 다른 점:
 *   저쪽은 견적서를 영업건으로 **승격**시키는 흐름(신규 등록/기존 갱신 + 값 매핑)이라 무겁다.
 *   여기는 「이미 있는 영업건 하나를 가리키기」만 한다 — 그래서 따로 뒀고, 다른 문서에서도 쓴다.
 *
 * 쓰는 쪽 계약:
 *   onPick(salesId)  — 고른 영업건 번호
 *   onUnlink?()      — 연동 풀기 (현재 연동이 있을 때만 버튼이 보인다)
 */

import { useEffect, useRef, useState } from 'react';
import { listSales } from '../../lib/sales-api';
import type { SalesListRow } from '../../lib/types';
import { LoadingLines } from '../../components/ui/Skeleton';

interface Props {
  open: boolean;
  /**
   * 문서의 사업명. **검색어로 미리 채우지 않는다** — 문서의 사업명과 영업건의 사업명이
   * 대개 다르게 적혀 있어서 열자마자 「0건」이 뜨고, 사용자에게는 **고장으로 보인다**
   * (2026-08-26 사용자 신고). 대신 위에 힌트로 보여 주고 [이 이름으로 찾기] 를 둔다.
   */
  docTitle?: string;
  /** 지금 연동돼 있는 영업건 번호 (없으면 null) */
  currentSalesId?: string | null;
  onPick: (salesId: string) => void | Promise<void>;
  onUnlink?: () => void | Promise<void>;
  onClose: () => void;
}

export function SalesPickerModal({
  open,
  docTitle = '',
  currentSalesId = null,
  onPick,
  onUnlink,
  onClose,
}: Props) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<SalesListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 창이 열릴 때 검색어는 **비운다** — 영업진행 화면처럼 목록이 바로 보이게.
  // deps 에 객체를 넣지 않는다(값으로).
  useEffect(() => {
    if (!open) return;
    setQ('');
    setErr(null);
    setBusy(false);
    // 열자마자 검색칸에 커서
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // 검색 — 타이핑이 멈추면 조회 (300ms)
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      // 검색어가 없으면 영업진행 화면과 같은 기본 목록(단계·최근 수정순)이 온다.
      listSales({ q: q.trim() || undefined, page: 1, pageSize: 50 })
        .then((res) => {
          if (!alive) return;
          setRows(res.data);
          setTotal(res.page.total);
          setErr(null);
        })
        .catch((e: unknown) => {
          if (!alive) return;
          setErr(e instanceof Error ? e.message : '영업건을 불러오지 못했습니다');
          setRows([]);
          setTotal(0);
        })
        .finally(() => alive && setLoading(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open, q]);

  if (!open) return null;

  const pick = async (salesId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await onPick(salesId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '연동에 실패했습니다');
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!onUnlink) return;
    setBusy(true);
    setErr(null);
    try {
      await onUnlink();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '연동 해제에 실패했습니다');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      // 배경 클릭으로 닫기 — mousedown 으로 판정해야 목록에서 드래그하다 놓은 게 닫기로 오인되지 않는다
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded shadow-xl w-[760px] max-w-[95vw] max-h-[88vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="ui-subsection-title">영업건 연동</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              NAS 보관 폴더가 영업건 기준이라, 연동해야 저장할 수 있습니다.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="매출일련번호 · 사업명 · 제안업체 · 엔드유저 · 계산서 발행업체로 검색 (비우면 전체)"
              className="ui-input flex-1"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                className="btn btn-secondary"
              >
                전체 보기
              </button>
            )}
          </div>

          {/* 문서의 사업명은 힌트로만 — 검색어로 미리 채우면 대개 0건이 떠서 고장으로 보인다 */}
          {docTitle && (
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
              <span>이 문서:</span>
              <span className="text-slate-700 truncate max-w-[38ch]" title={docTitle}>{docTitle}</span>
              <button
                type="button"
                onClick={() => setQ(docTitle)}
                className="btn btn-secondary btn-sm"
              >
                이 이름으로 찾기
              </button>
            </div>
          )}

          {currentSalesId && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-slate-500">지금 연동:</span>
              <span className="font-mono font-semibold text-slate-700">{currentSalesId}</span>
              {onUnlink && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void unlink()}
                  className="btn btn-secondary btn-sm ml-1"
                >
                  연동 풀기
                </button>
              )}
            </div>
          )}
        </div>

        {err && (
          <div className="mx-5 mt-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
            {err}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && rows.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center"><LoadingLines /></p>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-slate-600">
                {q ? (
                  <>
                    <span className="font-medium">「{q}」</span> 로 찾은 영업건이 없습니다.
                  </>
                ) : (
                  '영업건이 없습니다.'
                )}
              </p>
              {q && (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  className="btn btn-secondary mt-3"
                >
                  검색어를 지우고 전체 보기
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-1.5 pr-2 font-medium">매출일련번호</th>
                  <th className="py-1.5 pr-2 font-medium">제안업체</th>
                  <th className="py-1.5 pr-2 font-medium">사업명</th>
                  <th className="py-1.5 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isCurrent = r.salesId === currentSalesId;
                  return (
                    <tr
                      key={r.salesId}
                      className={`border-b border-slate-100 ${isCurrent ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="py-1.5 pr-2 font-mono text-xs whitespace-nowrap align-top">{r.salesId}</td>
                      <td className="py-1.5 pr-2 text-xs whitespace-nowrap align-top">{r.proposalCompany ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-xs align-top">
                        <div>{r.projectName ?? '—'}</div>
                        {/* 엔드유저를 같이 보여 준다 — 사업명만으로는 어느 건인지 가려지지 않는 때가 많다 */}
                        {r.endUser && <div className="text-xs text-slate-500">{r.endUser}</div>}
                      </td>
                      <td className="py-1.5 text-right">
                        <button
                          type="button"
                          disabled={busy || isCurrent}
                          onClick={() => void pick(r.salesId)}
                          className="btn btn-primary btn-sm"
                        >
                          {isCurrent ? '연동됨' : '연동'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-2.5 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <span>
            {total > rows.length
              ? `전체 ${total.toLocaleString()}건 중 ${rows.length}건 표시 — 찾는 건이 안 보이면 검색어를 넣어 주세요`
              : `${rows.length}건`}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
